import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import useUrlState from "@ahooksjs/use-url-state";
import MeaPage from "../../../components/MeaPage";
import TeacherDetailComponent from "./components/TeacherDetailComponent";
import teachersAPI from "../../../services/teachers";
import sessionsAPI from "../../../services/sessions";
import MeaPageTitle from "../../../components/MeaPageTitle";
import TeacherDetailAvatarComponent from "./components/TeacherDetailAvatarComponent";
import MeaPageHeader from "../../../components/MeaPageHeader";
import { TabMenu } from "primereact/tabmenu";
import { useEffect, useMemo, useRef } from "react";
import ScheduleListComponent from "../schedule/components/ScheduleListComponent";
import SessionsListComponent from "../sessions/components/SessionsListComponent";
import MeaLoadingContent from "../../../components/MeaLoadingContent";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { PERMISSION_TRAINING_TEACHERS } from "../../../permissions";
import UpdateTeacherMemberModal from "./components/UpdateTeacherMemberModal";
import { Toast } from "primereact/toast";
import { SelectButton } from "primereact/selectbutton";
import { ToFormatedShortISODate } from "../../../utils/dates";
import MeaPermissionGuard from "../../../components/MeaPermissionGuard";
import { isSessionOnOrBeforeToday } from "./teacherSessionStats";

const TeacherDetailPageContent = () => {
  const { hasPermission } = useAuthContext();
  const canUpdateTeacher = hasPermission(PERMISSION_TRAINING_TEACHERS.UPDATE);

  const { training } = useLoaderData();
  const navigate = useNavigate();
  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.trainingId;
  const accessToken = "";
  const teacherId = params.teacherId;

  const teacherQuery = teachersAPI.GetOne(
    schoolId,
    trainingId,
    accessToken,
    teacherId
  );
  const teacherObj = teacherQuery?.data?.data;

  const [state, setState] = useUrlState(
    {
      teacherTab: "0",
      teacherMonth: "",
    },
    { navigateMode: "replace" }
  );
  const listRef = useRef();
  const updateModalRef = useRef();
  const toast = useRef();

  const monthOptions = useMemo(() => {
    const startDate = training?.start_courses_date ? new Date(training.start_courses_date) : new Date();
    const endDate = training?.end_courses_date ? new Date(training.end_courses_date) : new Date();
    const months = [];
    const loopDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    while (loopDate <= endDate) {
      const year = loopDate.getFullYear();
      const month = loopDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      months.push({
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        value: `${year}-${String(month + 1).padStart(2, "0")}`,
        start_date: ToFormatedShortISODate(firstDay),
        end_date: ToFormatedShortISODate(lastDay),
      });

      loopDate.setMonth(loopDate.getMonth() + 1);
    }

    if (months.length === 0) {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      months.push({
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        value: `${year}-${String(month + 1).padStart(2, "0")}`,
        start_date: ToFormatedShortISODate(firstDay),
        end_date: ToFormatedShortISODate(lastDay),
      });
    }

    return months;
  }, [training?.start_courses_date, training?.end_courses_date]);

  const currentMonthValue = useMemo(() => {
    const currentDate = new Date();
    return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const activeItem = Number(state.teacherTab || 0);
  const selectedMonth = state.teacherMonth || null;
  const selectedMonthData = monthOptions.find((month) => month.value === selectedMonth) || monthOptions[0];

  useEffect(() => {
    if (!monthOptions[0]) {
      return;
    }

    if (!selectedMonth) {
      const defaultMonth =
        monthOptions.find((month) => month.value === currentMonthValue) || monthOptions[0];
      setState((currentState) => ({
        ...currentState,
        teacherMonth: defaultMonth.value,
      }));
      return;
    }

    const selectedMonthStillAvailable = monthOptions.some((month) => month.value === selectedMonth);
    if (!selectedMonthStillAvailable) {
      setState((currentState) => ({
        ...currentState,
        teacherMonth: monthOptions[0].value,
      }));
    }
  }, [currentMonthValue, monthOptions, selectedMonth, setState]);

  
  const monthlySessionsFilter = useMemo(() => ({
    start_date: selectedMonthData?.start_date,
    end_date: selectedMonthData?.end_date,
  }), [selectedMonthData?.start_date, selectedMonthData?.end_date]);

  const sessionsQuery = sessionsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    monthlySessionsFilter
  );

  const plannedSessionsCount = useMemo(() => {
    const total = (sessionsQuery?.data?.data || []).filter((session) => {
      const teacherId = session?.schedule_item?.teacher?.id || session?.schedule_item?.teacher_id;
      return teacherId === teacherObj?.id;
    }).length;
    return total;
  }, [sessionsQuery?.data?.data, teacherObj?.id]);

  const effectiveSessionsCount = useMemo(() => {
    const total = (sessionsQuery?.data?.data || []).filter((session) => {
      if (!isSessionOnOrBeforeToday(session)) return false;
      const teacherId = session?.teacher?.id || session?.teacher_id || session?.schedule_item?.teacher?.id || session?.schedule_item?.teacher_id;
      return teacherId === teacherObj?.id;
    }).length;
    return total;
  }, [sessionsQuery?.data?.data, teacherObj?.id]);

  const effectivePointsTotal = useMemo(() => {
    return (sessionsQuery?.data?.data || []).reduce((total, session) => {
      if (!isSessionOnOrBeforeToday(session)) return total;
      const teacherId = session?.teacher?.id || session?.teacher_id || session?.schedule_item?.teacher?.id || session?.schedule_item?.teacher_id;
      if (teacherId !== teacherObj?.id) return total;
      return total + (Number(session?.points) || 0);
    }, 0);
  }, [sessionsQuery?.data?.data, teacherObj?.id]);

const items = [
  { label: "Séances de cours", icon: "pi pi-calendar" },
  { label: "Programme de la semaine", icon: "pi pi-clock" },
  ];

  return (
    <>
      <Toast ref={toast} />
      <UpdateTeacherMemberModal ref={updateModalRef} teacher={teacherObj} />

      <MeaPage
        showBackButton={true}
        onBackButtonClick={() => navigate(`/school/${schoolId}/admin/trainings/${trainingId}/teachers`)}
        title={
          <MeaPageTitle
            prefix={"Enseignant(e):"}
            title={
              teacherObj?.member?.name ||
              teacherObj?.member?.lastname + " " + teacherObj?.member?.firstname
            }
          />
        }
        actions={[
          {
            label: "Changer de membre",
            icon: "pi pi-user-edit",
            disable: !canUpdateTeacher,
            command: () => {
              if (updateModalRef.current) {
                updateModalRef.current.show(() => {
                  teacherQuery.refetch();
                });
              }
            },
          },
        ]}
      >
        <MeaPageHeader>
          <TeacherDetailComponent teacher={teacherObj} />
          <TeacherDetailAvatarComponent teacher={teacherObj} />
        </MeaPageHeader>

        <div className="mt-5">
          <TabMenu
            model={items}
            activeIndex={activeItem}
            onTabChange={(e) => {
              setState((currentState) => ({
                ...currentState,
                teacherTab: String(e.index),
              }));
            }}
          />
          {activeItem === 0 && (
            <>
              <div className="grid mt-3 mb-3">
                <div className="col-12 md:col-4">
                  <div className="surface-card shadow-1 border-round p-3">
                    <div className="text-500 text-sm">Séances prévues du mois</div>
                    <div className="text-3xl font-semibold mt-2">{plannedSessionsCount}</div>
                  </div>
                </div>
                <div className="col-12 md:col-4">
                  <div className="surface-card shadow-1 border-round p-3">
                    <div className="text-500 text-sm">Séances effectives du mois</div>
                    <div className="text-3xl font-semibold mt-2">{effectiveSessionsCount}</div>
                  </div>
                </div>
                <div className="col-12 md:col-4">
                  <div className="surface-card shadow-1 border-round p-3">
                    <div className="text-500 text-sm">Total des points du mois</div>
                    <div className="text-3xl font-semibold mt-2">
                      {new Intl.NumberFormat("fr-FR", {
                        maximumFractionDigits: 2,
                      }).format(effectivePointsTotal)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <SelectButton
                  value={selectedMonth || selectedMonthData?.value}
                  options={monthOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) =>
                    setState((currentState) => ({
                      ...currentState,
                      teacherMonth: e.value || "",
                    }))
                  }
                  className="w-full flex-wrap"
                />
              </div>
              <MeaLoadingContent objectQuery={teacherQuery}>
                <SessionsListComponent
                  key={`${teacherId}-${selectedMonthData?.value || "all"}`}
                  ref={listRef}
                  schoolId={schoolId}
                  trainingId={trainingId}
                  currentTeacherId={teacherObj?.id}
                  filter={{
                    teacher_id: teacherObj?.id,
                    start_date: selectedMonthData?.start_date,
                    end_date: selectedMonthData?.end_date,
                  }}
                  showAddButton={false}
                  showFilterButton={false}
                  showAllSessions={true}
                  selectedFields={{
                    presence: true,
                    replacement: true,
                    teacher: false,
                    edit: false,
                  }}
                />
              </MeaLoadingContent>
            </>
          )}
          {activeItem === 1 && (
            <MeaLoadingContent objectQuery={teacherQuery}>
              <ScheduleListComponent
                ref={listRef}
                schoolId={schoolId}
                trainingId={trainingId}
                filter={{ teacher_id: teacherObj?.id }}
                showAddButton={false}
                showFilterButton={true}
                selectedFields={{
                  teacher: false,
                }}
              />
            </MeaLoadingContent>
          )}
        </div>
      </MeaPage>
    </>
  );
};

const TeacherDetailPage = () => {
  const { schoolId, trainingId } = useParams();

  return (
    <MeaPermissionGuard
      permission={PERMISSION_TRAINING_TEACHERS.GET}
      fallbackTo={`/school/${schoolId}/admin/trainings/${trainingId}/teachers`}
    >
      <TeacherDetailPageContent />
    </MeaPermissionGuard>
  );
};

export default TeacherDetailPage;
